import { UAContext } from "@quentin-sommer/react-useragent";
import { type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import {
  redirect,
  useLoaderData,
  useNavigate,
  useParams,
} from "@remix-run/react";
import { useDebounce } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import lodash from "lodash";
import qs from "qs";
import React from "react";
import { useTranslation } from "react-i18next";
import * as cookies from "~/cookies";
import toastWithStyle from "~/helpers/toastWithStyle";
import { fallbackLng, supportedLngs } from "~/i18n";
import i18nServer from "~/i18n.server";
import ServicesServiceScreen from "~/screens/services.$sid";
import { getListResources, getLookupResource, getMe } from "~/services/api";
import splitIntoSegmentsAndGetMidpoints from "~/utils/splitIntoSegmentsAndGetMidpoints";
import to from "~/utils/to";

export async function loader({ params, request }: LoaderFunctionArgs) {
  let cookie = await cookies.auth.find(request);
  const locale = await i18nServer.getLocale(request);
  const preferredLng = locale;
  const t = await i18nServer.getFixedT(preferredLng);

  // if there is no selected language in url, redirect to the default language
  if (!params.lng) {
    const url = new URL(request.url);
    const newUrl = new URL(
      `/${preferredLng}/services/${params.sid}/`,
      url.origin,
    );
    newUrl.search = url.search; // Preserve query strings
    return redirect(newUrl.toString(), { status: 301 });
  }

  if (!supportedLngs.includes(params.lng)) {
    // return 404 if the language is not supported
    throw new Response("Not Found", { status: 404 });
  }

  const date0 = new Date();

  if (cookie) {
    if (!cookie.user.settings?.currency || !cookie.user.settings?.language) {
      const url = new URL(request.url);
      throw redirect(
        "/init" + `?${qs.stringify({ next: url.pathname + url.search })}`,
      );
    }
  }

  const headers = { "axios-to-fetch": 1, "preferred-lng": preferredLng };

  const baseQueries = [
    getListResources({
      token: cookie?.token,
      resource: "providers",
      query: {
        status: "available",
        projection: "name _id id",
        sort: { createdAt: 1 },
      },
      headers,
    }),
    getLookupResource({
      token: cookie?.token,
      resource: "services",
      query: {
        _id: params.sid,
        projection: "name _id id metadata.redirect content translations media",
        cache: 600,
      },
      headers,
    }),
    getListResources({
      token: cookie?.token,
      resource: "products",
      query: {
        price: ">0",
        "inventory.quantity": ">0",
        status: "available",
        population: "_service _country _provider",
        _service: params.sid,
        projection: "-metadata",
        sort: { "inventory.quality": -1, "inventory.quantity": -1 },
        limit: 1,
        cache: 600,
      },
      headers,
    }),
  ];

  if (cookie) {
    baseQueries.push(
      getMe({ token: cookie.token }),
      getLookupResource({
        token: cookie.token,
        resource: "currencies",
        query: { code: cookie.user.settings.currency },
        headers,
      }),
      getListResources({
        token: cookie?.token,
        resource: "operations",
        query: {
          createdAt: `>${dayjs().subtract(1, "day").toISOString()}`,
          _service: params.sid,
          limit: 3,
          status: "completed",
        },
        headers,
      }),
    );
  }

  const [providers, service, products, user, currency, operations] =
    await Promise.all(baseQueries);

  const date1 = new Date();

  if (service.data.metadata?.redirect?.to) {
    return redirect(service.data.metadata.redirect.to, {
      status: service.data.metadata.redirect.status || 301,
    });
  }

  const providerProductsResults = await Promise.all(
    providers.data.results.map((provider) =>
      getListResources({
        token: cookie?.token,
        resource: "products",
        query: {
          _provider: provider._id,
          price: ">0",
          "inventory.quantity": ">0",
          status: "available",
          population: "_service _country _provider",
          _service: params.sid,
          projection: "-metadata",
          sort: { "inventory.quality": -1, "inventory.quantity": -1 },
          limit: 8,
          cache: 600,
        },
        headers,
      }),
    ),
  );

  const allProviderProducts = providerProductsResults.flatMap(
    (result) => result.data.results,
  );

  let latestCompletedProducts = { data: { results: [] } };
  if (!lodash.isEmpty(operations?.data?.results)) {
    const latestCompletedProductIds =
      operations.data.results?.map((o) => o._product) || [];
    if (!lodash.isEmpty(latestCompletedProductIds)) {
      latestCompletedProducts = await getListResources({
        token: cookie?.token,
        resource: "products",
        query: {
          "_id[]": latestCompletedProductIds,
          population: "_country _service",
        },
        headers,
      });
    }
  }

  const date2 = new Date();
  return Response.json({
    providers: providers.data,
    service: service.data,
    products: {
      ...products.data,
      results: allProviderProducts.filter(
        (p) => p._provider?.status === "available",
      ),
    },
    user: user?.data.user,
    currency: currency?.data,
    latestCompletedProducts: latestCompletedProducts?.data,
    cookie,
    params,
    inUrlQs: qs.parse(request.url.split("?")[1]),
    headers,
    dates: {
      diff1: date1.getTime() - date0.getTime(),
      diff2: date2.getTime() - date1.getTime(),
    },
    strings: {
      title: `${t("buyVirtualNumbersFor", { name: lodash.capitalize(service.data.name.common) })} | ${t("title")}`,
      description: t("description"),
      og: {
        title: `${t("og.buyVirtualNumbersFor", { name: lodash.capitalize(service.data.name.common) })} | ${t("og.title")}`,
        description: t("og.description"),
      },
    },
    locale,
  });
}

export const meta: MetaFunction = ({ data }) => {
  const detectedLng = data.locale;
  const canonicalUrlWithLng = `https://hidsim.com/${detectedLng}/services/${data.service._id}/`;
  const baseUrlWithLng = `https://hidsim.com/${detectedLng}/services/${data.service._id}/`;

  return [
    { title: data.strings.title },
    { name: "description", content: data.strings.description },
    { property: "og:title", content: data.strings.og.title },
    { property: "og:description", content: data.strings.og.description },
    { property: "og:url", content: canonicalUrlWithLng },
    {
      property: "og:image",
      content: data?.service?.media?.feature
        ? `${data.service.media?.feature}?format=webp&width=1200&height=630`
        : "https://s3.hidsim.com/services/general_feature_1.png",
    },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    {
      property: "twitter:image",
      content: data?.service?.media?.feature
        ? `${data.service.media?.feature}?format=webp&width=300&height=157`
        : "https://s3.hidsim.com/services/general_feature_1.png",
    },
    { property: "og:locale", content: detectedLng },
    {
      tagName: "link",
      rel: "canonical",
      href: canonicalUrlWithLng,
      key: baseUrlWithLng,
    },
    ...supportedLngs.map((lng) => ({
      tagName: "link",
      rel: "alternate",
      hrefLang: lng,
      href: `https://hidsim.com/${lng}/services/${data.service._id}/`,
      key: `https://hidsim.com/${lng}/services/${data.service._id}/`,
    })),
  ];
};

function ServicesService() {
  const { inUrlQs, products, cookie, headers, dates, serviceJsonLd } =
    useLoaderData<typeof loader>();
  const { uaResults } = React.useContext(UAContext);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { sid } = useParams();
  const [textQuery, setTextQuery] = React.useState(inUrlQs.text || "");
  const [sortFilter, setSortFilter] = React.useState("quality-high-to-low");
  const debouncedTextQuery = useDebounce(textQuery, 1000);
  const [r1, r2, r3] = splitIntoSegmentsAndGetMidpoints({
    numbers: products.results.map((r) => r.inventory?.quality),
    segmentsCount: 3,
  });
  const [loadedProducts, setLoadedProducts] = React.useState(products);
  const [loading, setLoading] = React.useState(false);
  console.log({ dates });

  const loadAsyncData = async () => {
    let success = false;
    while (!success) {
      setLoading(true);
      const [err, data] = await to(
        getListResources({
          token: cookie?.token,
          resource: "products",
          query: {
            price: ">0",
            "inventory.quantity": ">0",
            status: "available",
            population: "_service _country _provider",
            _service: sid,
            projection: "-metadata",
            sort: { "inventory.quality": -1, "inventory.quantity": -1 },
            limit: 400,
            cache: 600,
          },
          headers: { ...headers, "axios-to-fetch": 0 },
        }),
      );
      setLoading(false);
      if (err) {
        toastWithStyle({
          message: err.message,
          i18n,
          options: { type: "error" },
          uaResults,
        });
      } else {
        setLoadedProducts((prev) => ({
          ...prev,
          results: lodash.uniqBy(
            [...prev.results, ...data.data.results],
            (p) => p._id,
          ),
        }));
        success = true;
      }
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      loadAsyncData();
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    const currenQs = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    const currentTextQuery = currenQs.text || "";
    if (currentTextQuery === debouncedTextQuery) return;
    if (!debouncedTextQuery) navigate("/services/" + sid);
    navigate(
      "/services/" + sid + "?" + qs.stringify({ text: debouncedTextQuery }),
    );
  }, [debouncedTextQuery]);

  return (
    <ServicesServiceScreen
      textQuery={textQuery}
      setTextQuery={setTextQuery}
      sortFilter={sortFilter}
      setSortFilter={setSortFilter}
      ranges={[r1, r2, r3]}
      loading={loading}
      products={loadedProducts}
    />
  );
}

export default ServicesService;
